import React from 'react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

const STUB_CONTEXT = { anonymous: true, kind: 'user' };

// Keys are the raw LaunchDarkly flag names that launchDarklyToggles maps onto
// DARKLY_TOGGLE_NAMES, not the toggle names themselves.
const E2E_FLAGS = {
  isEnterpriseDashboardEnabled: true,
  dashboardToggle: true,
  // Gates the /timekeeping route, which hosts the Sage payroll dialog.
  timesheetToggle: true,
};

function getStubFlags() {
  if (process.env.REACT_APP_E2E_LD_STUB === 'true') {
    return { ...E2E_FLAGS };
  }
  return {};
}

/**
 * In-process stand-in for launchdarkly-js-client-sdk's LDClient when `ldClient` is passed to
 * launchdarkly-react-client-sdk's LDProvider. No network I/O (local dev / E2E without client ID).
 *
 * LDProvider v3 mainly uses allFlags, variation (if sendEventsOnFlagRead), on/off('change').
 * Exposing the rest of LDClientBase avoids runtime errors if the SDK, HOCs, or app code call
 * waitForInitialization, identify, close, etc. after upgrades or via useLDConsumer props.
 */
function createStubLdClient() {
  const emptyFlags = () => getStubFlags();

  return {
    allFlags: emptyFlags,
    variation: (_key, defaultValue) => defaultValue,
    variationDetail: (_key, defaultValue) => ({ value: defaultValue }),
    on() {},
    off() {},
    waitUntilReady: () => Promise.resolve(),
    waitForInitialization: () => Promise.resolve(),
    waitUntilGoalsReady: () => Promise.resolve(),
    identify(_context, _hash, onDone) {
      const flags = emptyFlags();
      if (typeof _hash === 'function') {
        _hash(null, flags);
        return undefined;
      }
      if (typeof onDone === 'function') {
        onDone(null, flags);
        return undefined;
      }
      return Promise.resolve(flags);
    },
    getContext: () => STUB_CONTEXT,
    flush(onDone) {
      if (typeof onDone === 'function') {
        onDone();
        return undefined;
      }
      return Promise.resolve();
    },
    setStreaming() {},
    track() {},
    close(onDone) {
      if (typeof onDone === 'function') {
        onDone();
        return undefined;
      }
      return Promise.resolve();
    },
  };
}

const stubLdClientSingleton = createStubLdClient();

/**
 * Root provider when `REACT_APP_LAUNCHDARKLY_CLIENT_ID` is unset. Feature flags resolve to
 * app defaults via `launchDarklyToggles` (empty `flags` object).
 */
const StubLdRootProvider = ({ children }) => (
  <LDProvider
    clientSideID="local-stub-no-network"
    ldClient={stubLdClientSingleton}
    reactOptions={{ sendEventsOnFlagRead: false }}
  >
    {children}
  </LDProvider>
);

StubLdRootProvider.propTypes = {
  children: PropTypes.node,
};

export default StubLdRootProvider;
